import React, {useState} from "react";
import {Edit} from "feather-icons-react/build/IconComponents";
import * as Tooltip from "@radix-ui/react-tooltip";
import NumberUtils from "../../helpers/numberUtils";

export const EditableField = ({label, value, onSave, options = [], isLastItem = false, isQuestion}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value ?? "");
  const numberUtils = new NumberUtils();

  let className = "flex justify-between py-2 border-b border-slate-300";
  if (isLastItem) {
    className = "flex justify-between pt-2";
  }
  if (isQuestion) {
    className = "flex flex-col py-2 border-slate-300 gap-2 w-full";
  }

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const dateParts = dateValue.split("-");
    if (dateParts.length !== 3) return "N/A";

    const year = dateParts[0];
    const month = dateParts[1].padStart(2, "0");
    const day = dateParts[2].padStart(2, "0");

    return `${month}/${day}/${year}`;
  };

  const toDateInputFormat = (dateValue) => {
    if (!dateValue) return "";
    const dateObj = new Date(dateValue + "T00:00:00");
    if (isNaN(dateObj.getTime())) return "";
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const day = dateObj.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleSave = async () => {
    let finalValue = tempValue;

    if ((label === "Effective Date" || label === "Renewal Date") && tempValue) {
      const dateParts = tempValue.split("-");
      if (dateParts.length === 3) {
        finalValue = tempValue;
      } else {
        finalValue = null;
      }
    }

    if (label === "Total Cost") {
      finalValue = parseFloat(tempValue.replace(/[^0-9.]/g, "")) || 0;
    }

    await onSave(finalValue);
    setIsEditing(false);
  };

  return (
    <div className={className}>
      {isQuestion ? (
        // Special layout for question fields
        <>
          <div className="font-medium text-gray-700">{label}</div>
          {isEditing ? (
            <div className="flex flex-col gap-3">
              <textarea
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value || "")}
                className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full h-20 resize-none"
                placeholder="Enter your answer..."
              />
              <div className="flex items-center gap-2 self-end">
                <button 
                  onClick={handleSave} 
                  className="px-3 py-1 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  Save
                </button>
                <button 
                  onClick={() => setIsEditing(false)} 
                  className="px-3 py-1 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div className="flex-1 pr-10">
                <Tooltip.Provider>
                  <Tooltip.Root>
                    <Tooltip.Trigger
                      className="text-sm text-gray-600 cursor-pointer hover:underline text-left w-full"
                    >
                      {value?.trim() !== "" ? (
                        value.length > 100 ? value.substring(0, 100) + "..." : value
                      ) : "No answer provided"}
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content className="max-w-[400px] bg-white p-3 shadow-lg rounded-lg border">
                        {value || "No answer provided"}
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              </div>
              <button 
                onClick={() => setIsEditing(true)} 
                className="flex-shrink-0 p-1 text-gray-500 hover:text-blue-500 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Edit"
              >
                <Edit size={16}/>
              </button>
            </div>
          )}
        </>
      ) : (
        // Regular layout for normal fields
        <>
          <div className="font-medium text-gray-700 w-1/3">{label}</div>
          <div className="flex items-center w-2/3">
            {isEditing ? (
              <div className="flex items-center w-full relative">
                <div className="flex-1 pr-32">
                  {label === "Agency Name" || label === "Name of Insured" ? (
                    <select
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      className="border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-[200px]"
                    >
                      <option value="">Select a {label}</option>
                      {options.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={label === "Effective Date" || label === "Renewal Date" ? "date" : "text"}
                      value={
                        label === "Effective Date" || label === "Renewal Date"
                          ? toDateInputFormat(tempValue)
                          : label === "Total Cost"
                            ? tempValue
                            : tempValue
                      }
                      onChange={(e) => setTempValue(e.target.value || "")}
                      className="border border-slate-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-[200px]"
                    />
                  )}
                </div>
                <div className="absolute right-0 flex items-center gap-2">
                  <button 
                    onClick={handleSave} 
                    className="px-3 py-1 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Save
                  </button>
                  <button 
                    onClick={() => setIsEditing(false)} 
                    className="px-3 py-1 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center w-full relative">
                <div className="flex-1 pr-10 text-left">
                  <span>
                    {label === "Effective Date" || label === "Renewal Date"
                      ? formatDate(value)
                      : label === "Total Cost"
                        ? numberUtils.formatToCurrency(value)
                        : value || ""}
                  </span>
                </div>
                <button 
                  onClick={() => setIsEditing(true)} 
                  className="absolute right-0 p-1 text-gray-500 hover:text-blue-500 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Edit"
                >
                  <Edit size={16}/>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
